import { useAuth } from 'react-oidc-context';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { UnauthorisedResponse } from '~/lib/api/unauthorised-response';
import { getApiPath } from './get-api-path';
import { ApiResponse } from '../api-response';
import { authedFetch } from '../authed-fetch';
import { parseResult } from '../parse-result';
import {
  ActionType,
  GetInstructionsApiResponseModel,
  Integration,
  LinkAccountResponseApiModel,
  SettingsApiResponseModel,
  StrVal
} from './types';
import { GetBaseUrl } from '~/apps/account-linking/lib/helpers/GetBaseUrl';
import { useCallback } from 'react';

export interface IApiResponse<T> {
  success: boolean;
  result: T | null;
}

export function useAccountLinkingApi() {
  const { user } = useAuth();
  const { tenant } = useTenant();

  const getintegrationsummary = useCallback(async (): Promise<ApiResponse<Integration[]>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }

    const url = getApiPath(tenant, `integrations`);
    const response = await authedFetch(tenant, url, { method: 'GET' }, user?.access_token);
    return parseResult<Integration[]>(response);
  }, [user?.access_token, tenant]);

  const postInternalAuthCallbackUrl = useCallback(
    async (internalAuthCallbackUrl: string): Promise<ApiResponse<{ redirectUrl: string }>> => {
      const response = await authedFetch(
        tenant,
        internalAuthCallbackUrl,
        {
          method: 'POST',
          body: JSON.stringify({
            ReturnUrl: GetBaseUrl()
          })
        },
        user?.access_token
      );
      return parseResult<{ redirectUrl: string }>(response);
    },
    [user?.access_token, tenant]
  );

  const getActionInstructions = useCallback(
    async (
      integrationsType: string,
      slug: string,
      action: ActionType,
      accountId?: string
    ): Promise<ApiResponse<GetInstructionsApiResponseModel>> => {
      if (!user?.access_token) {
        return UnauthorisedResponse;
      }

      const url = accountId
        ? getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}/instructions/${accountId}`)
        : getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}/instructions`);

      const response = await authedFetch(tenant, url, { method: 'GET' }, user?.access_token);

      return parseResult<GetInstructionsApiResponseModel>(response);
    },
    [user?.access_token, tenant]
  );

  const getSettings = useCallback(async (): Promise<ApiResponse<SettingsApiResponseModel>> => {
    if (!user?.access_token) {
      return UnauthorisedResponse;
    }

    try {
      const url = getApiPath(tenant, `setup`);
      const response = await authedFetch(tenant, url, {}, user?.access_token);
      return parseResult<SettingsApiResponseModel>(response);
    } catch {
      return { success: false } as ApiResponse<any>;
    }
  }, [user?.access_token, tenant]);

  const postActionValues = useCallback(
    async (
      integrationsType: string,
      slug: string,
      action: ActionType,
      model: StrVal,
      accountId?: string
    ): Promise<ApiResponse<LinkAccountResponseApiModel>> => {
      // Add return URL for Links and Renews
      if (action == 'Link' || action == 'Renew') {
        model['ReturnUrl'] = GetBaseUrl();
      }

      // If there's a referrer stored in local storage check if it matches the current integration and action, if so add it to the values - filtering to allowed actions will happen in edge
      const referrerId =
        localStorage.getItem(`accountLinking_referrer_${slug}_${action}`.toLowerCase()) ??
        localStorage.getItem(`accountLinking_referrer_global_${action}`.toLowerCase()) ??
        localStorage.getItem(`accountLinking_referrer_global`.toLowerCase());
      if (referrerId) {
        model['ReferrerId'] = referrerId;
      }

      const url = accountId
        ? getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}/${accountId}`)
        : getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(model) },
        user?.access_token
      );
      return parseResult<LinkAccountResponseApiModel>(response);
    },
    [user?.access_token, tenant]
  );

  const getMarketplaceRedirectUrl = useCallback(
    async (slug: string) => {
      const sanitisedSlug = slug.toLowerCase();
      const action = sanitisedSlug === 'ebay' ? 'Link' : 'Install';
      const res = await postActionValues('Marketplace', sanitisedSlug, action, {});
      if (res.success && res.result.redirectUrl) {
        return res.result.redirectUrl;
      }
      return '';
    },
    [postActionValues]
  );

  return {
    getintegrationsummary,
    postInternalAuthCallbackUrl,
    getActionInstructions,
    getSettings,
    postActionValues,
    getMarketplaceRedirectUrl
  } as const;
}
